<template>
    <div>
        <div class="notifications-container">
            <template
                v-for="notification in activeNotificationStore.notifications"
                :key="notification.id"
            >
                <transition>
                    <div
                        v-if="notification?.hidden != true"
                        class="notification"
                        :class="{
                            'bg-info': (notification.priority == 'low'),
                            'bg-warning': (notification.priority == 'medium'),
                            'bg-negative': (notification.priority == 'high'),
                            'text-white': (notification.priority != 'low'),
                        }"
                    >
                        <p class="q-ma-none q-pa-sm">
                            <q-btn
                                round
                                flat
                                icon="close"
                                @click="dismissAdminNotification(notification.id)"
                            />
                            <q-icon
                                v-if="notification.priority == 'low'"
                                name="info"
                                size="sm"
                            />
                            <q-icon
                                v-else-if="notification.priority == 'medium'"
                                name="warning"
                                size="sm"
                            />
                            <q-icon
                                v-else
                                name="crisis_alert"
                                size="md"
                            />
                            <!-- eslint-disable vue/no-v-html -->
                            <span
                                class="message"
                                v-html="DOMPurify.sanitize(marked.parse(notification.markdownMessage))"
                            />
                            <!-- eslint-enable vue/no-v-html -->
                        </p>
                    </div>
                </transition>
            </template>
        </div>
        <div>
            <router-view
                name="NavBar"
                @logout="logout()"
            />
            <div class="main-page height-limit">
                <router-view />
            </div>
        </div>
    </div>
</template>

<script setup>
import { RouterView, useRouter } from "vue-router";
import { computed, inject, onMounted, ref, watch } from 'vue';
import { Notify } from 'quasar';
import { marked } from 'marked'
import DOMPurify from 'dompurify';

import { getNewForecastAvailable } from '@/utils/api';
import { clearAuth } from '@/utils/auth';
import { clearStores } from "@/utils/storeHelpers";

import { isAuthenticated, isDemoUser } from '@/utils/auth';
import { useAssetStore } from "@/stores/assetStore";
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import { useForecastStore } from '@/stores/forecastStore';
import { useActiveNotificationsStore } from "@/stores/notificationStore";
import { useSettingStore } from "@/stores/settingStore";

const router = useRouter();
const deepState = inject('deepState');
const assetStore = useAssetStore();
const featureFlagStore = useFeatureFlagStore();
const activeNotificationStore = useActiveNotificationsStore();
const forecastStore = useForecastStore();
const settingStore = useSettingStore();
const pollInterval = 300000;
const newDataAvailable = ref(false);
const displayingNotification = ref(false);

const isOnDemoLanding = computed(() => router.currentRoute.value.name === 'demo');

// Watch for new forecast data availability and notify user to reload the page.
// This will reset the avilability to false, setting to true when another new
// forecast is available.
watch(() => newDataAvailable.value, (newval) => {
    forecastStore.newDataAvailable =  newval;
    if (newval) {
        displayingNotification.value = true;
        Notify.create({
            icon: 'mdi-weather-pouring',
            color: 'white',
            textColor: 'black',
            timeout: 0,
            message: 'There is a new forecast available. \nPlease reload the page for the most recent updates. ',
            actions: [
                {
                    label: 'Reload',
                    color: 'primary',
                    handler: () => window.location.reload(),
                    icon: 'refresh'
                },
                {
                    label: 'Dismiss',
                    color: 'primary',
                    handler: () => displayingNotification.value = false,
                    icon: 'close'
                },
            ],
            multiLine: true,
            position: 'bottom-right'
        });
    }
});

onMounted(async () => {
    try {
        if (isAuthenticated()) {
            await featureFlagStore.initializeFlagStore();
            if (!isOnDemoLanding.value) {
                await settingStore.initializeSettingStore().then(() => {
                    if (!settingStore.hasActiveSubscription) {
                        router.push('/subscribe');
                        throw "No Active Subscription"
                    }
                });
                assetStore.initializeAssetStore(deepState);
                forecastStore.initializeForecastStore();
                if(!isDemoUser()) {
                    activeNotificationStore.initializeActiveNotificationsStore();
                }
            }
        }
        pollForNewUpdatesInBackground();
    } catch(e) {
        console.error(e);
        if (e !== "No Active Subscription") {
            Notify.create({ type: 'negative', message: 'There was a problem loading data. Please try again later' });
        }
    }
});

const dismissAdminNotification = (nid) => {
    activeNotificationStore.hideNotification(nid);
}

const pollForNewUpdatesInBackground = () => {
    // Check if user's session is still valid
    setInterval(() => {
        if ((!isAuthenticated() && !displayingNotification.value && !['login', 'demo', 'forgot'].includes(router.currentRoute.value.name))) {
            displayingNotification.value = true;
            Notify.create({
                color: 'white',
                textColor: 'black',
                timeout: 0,
                message: `Your session has expired.`,
                actions: [
                    {
                        label: 'Login',
                        color: 'primary',
                        handler: () => logout(),
                        icon: 'login'
                    },
                    {
                        label: 'Dismiss',
                        color: 'primary',
                        handler: () => displayingNotification.value = false,
                        icon: 'close'
                    },
                ],
                multiLine: true,
                position: 'bottom-right'
            });
        }
    }, pollInterval);
    if (!forecastStore.lastForecast) return;
    if (!['flux-forecast', 'reports', 'settings', 'methodology'].includes(router.currentRoute.value.name)) return;
    const year = new Date(forecastStore.lastForecast).getUTCFullYear();
    const month = `${new Date(forecastStore.lastForecast).getUTCMonth() + 1 < 10 ? '0' : ''}${new Date(forecastStore.lastForecast).getUTCMonth() + 1}`;
    const date = `${new Date(forecastStore.lastForecast).getUTCDate() < 10 ? '0' : ''}${new Date(forecastStore.lastForecast).getUTCDate()}`;
    const hour = `${new Date(forecastStore.lastForecast).getUTCHours() < 10 ? '0' : ''}${new Date(forecastStore.lastForecast).getUTCHours()}`;
    const min = `${new Date(forecastStore.lastForecast).getUTCMinutes() < 10 ? '0' : ''}${new Date(forecastStore.lastForecast).getUTCMinutes()}`;
    const sec = `${new Date(forecastStore.lastForecast).getUTCSeconds() < 10 ? '0' : ''}${new Date(forecastStore.lastForecast).getUTCSeconds()}`;
    const dateString = `${year}-${month}-${date}T${hour}:${min}:${sec}`;
    setInterval(() => fetchNewForecastData(dateString), pollInterval);
};

const fetchNewForecastData = (dateString) => {
    if (new Date() >= new Date(forecastStore.nextForecast) && !displayingNotification.value) {
        getNewForecastAvailable(dateString).then((res) => {
            newDataAvailable.value = res;
        });
    }
};

const logout = () => {
    clearStores()
    clearAuth();
    deepState.clearAllStates();
    displayingNotification.value = false;
    router.push('/login');
};

</script>

<style lang="scss">
body,
html {
    color: $primary-font-color;
    background-color: white;
    font-size: $base-font-size;
    height: 100%;
    font-family: 'Inter', 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif, 'Inter' !important;
    overflow: hidden;
}
.main-page {
    width: calc(100vw - 65px);
    position:relative;
    left: 65px;
    @media screen and (max-width: $mobile-breakpoint) {
        width: 100vw;
        left: 0px;
    }
}
.notifications-container {
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100vw;
    z-index: 400;
    .notification {
        span.message {
            display: inline-block;
            margin-left: 5px;
            vertical-align: middle;
            p {
                margin: 2px;
            }
        }
    }
    /* simple fade in/out */
    .v-enter-active,
    .v-leave-active {
        transition: opacity 0.5s ease;
    }

    .v-enter-from,
    .v-leave-to {
        opacity: 0;
    }
}
</style>
